/**
 * Storage Outpost trade economy — per-resource sell/buy gold prices.
 *
 * KTS-faithful prices (EffectData.xml *SellPrice / *BuyPrice). v1 ships the full
 * KTS-8 outpost trade roster, split into two menu tabs by value tier:
 *   Normal — Lumber / Stone / Wool / Leather  (common gathered/farm goods)
 *   Rare   — Iron Ore / Shadowstone / Radiant Gem / Infused Lumber  (mined / boss /
 *            perk-drop / processed; Shadowstone + Radiant Gem are spell riders)
 * (Earlier this shipped only the 4 base resources — an over-cut from a stale
 * comment that conflated the outpost market with the separate Tavern
 * raw-resource-buyback cut in `docs/design/06-economy-gear.md`. The outpost
 * market was never cut; all 8 resources are live in v1.)
 *
 * The outpost is a market vendor, not a stockpile: SELL consumes 1 backpack
 * unit of the resource and credits gold; BUY debits gold and credits 1 unit.
 * Gold is minted/sunk by the market (the owner does NOT fund it). LockStorage
 * (owner toggle) blocks non-owners from BUYING only — selling is always allowed.
 * Profiteering (owner kickback per enemy purchase) is CUT for v1 (no matching
 * expertise perk in our B5 trees; decided 2026-06-18 pre-playtest review).
 *
 * Resource keys match ResourceBag fields (`shared/state.ts`) + RESOURCE_POLICY
 * so the server routes debit/credit through the material-flow helpers.
 *
 * Interconnections:
 *   - server/HelloRoom.ts building-action: `sell:<key>` / `buy:<key>` / `lockStorage`
 *   - client/buildings/building-menu.ts: tabbed Sell/Buy rows (Normal | Rare)
 *   - server/bots/rules/ai-trade-sell-store.ts: bot sells excess via `sell:<key>`
 */
export interface TradePrice {
  /** Gold the seller receives for 1 unit. */
  readonly sell: number;
  /** Gold the buyer pays for 1 unit. */
  readonly buy: number;
  /** Display label (short — fits the building-menu row). */
  readonly label: string;
}

/** All v1 tradable resource keys, in display order (Normal tab then Rare tab).
 *  Each is a ResourceBag key routed by RESOURCE_POLICY. */
export const TRADE_RESOURCES = [
  "lumber", "stone", "wool", "leather",
  "ironOre", "shadowstone", "radiantGem", "infusedLumber",
] as const;
export type TradeResource = (typeof TRADE_RESOURCES)[number];

export const TRADE_PRICES: Readonly<Record<TradeResource, TradePrice>> = {
  // Normal tab — common gathered/farm goods (tier-1, sell ≤ 12).
  lumber: { sell: 12, buy: 18, label: "Lumber" },
  stone: { sell: 10, buy: 15, label: "Stone" },
  wool: { sell: 10, buy: 15, label: "Wool" },
  leather: { sell: 10, buy: 15, label: "Leather" },
  // Rare tab — mined / boss / perk-drop / processed mats (tier 2-5, sell ≥ 20).
  ironOre: { sell: 20, buy: 30, label: "Iron" },
  shadowstone: { sell: 30, buy: 45, label: "Shadowstone" },
  radiantGem: { sell: 40, buy: 60, label: "Radiant Gem" },
  infusedLumber: { sell: 50, buy: 75, label: "Infused Lumber" },
};

/** Menu tab grouping for the Storage Outpost trade UI. The client renders one
 *  tab per entry; the server/bot ignore grouping (they read TRADE_PRICES flat). */
export const TRADE_GROUPS: ReadonlyArray<{
  readonly id: "normal" | "rare";
  readonly label: string;
  readonly resources: readonly TradeResource[];
}> = [
  { id: "normal", label: "Normal", resources: ["lumber", "stone", "wool", "leather"] },
  { id: "rare", label: "Rare", resources: ["ironOre", "shadowstone", "radiantGem", "infusedLumber"] },
];

/** Type guard: is `key` a tradable resource with a price row? */
export function isTradeResource(key: string): key is TradeResource {
  return key in TRADE_PRICES;
}

/** Parse a `sell:<key>` / `buy:<key>` building-action into its parts, or null. */
export function parseTradeAction(
  action: string,
): { side: "sell" | "buy"; key: TradeResource } | null {
  const m = /^(sell|buy):(.+)$/.exec(action);
  if (!m) return null;
  const key = m[2] ?? "";
  if (!isTradeResource(key)) return null;
  return { side: m[1] as "sell" | "buy", key };
}
